import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
  Slot,
} from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import UserList from "./UserList";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { User } from "@/models/users/User";
import { FC, useEffect, useState, useRef } from "react";
import { FeatureFlag, Group } from "@/models/feature/feature";
import { UserPlus, UserX, X } from "lucide-react";
import useFeatureStore from "@/stores/feature.store";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@capacit-com/lara-law-shared-ui";
import {
  dialogContentStyle,
  dialogHeaderStyle,
  dialogDescriptionStyle,
  dialogDescriptionWrapperStyle,
  userListParentWrapperStyle,
  userListCardWrapperLeft,
  userListCardWrapperRight,
  userListCardStyle,
  userListCardHeaderStyle,
  userListCardHeaderTitleTextStyle,
  userListCardHeaderTitleTooltipStyle,
  userListCardHeaderAbsoluteButtonWrapperStyle,
  userListCardHeaderAbsoluteButtonStyle,
  userListComponentStyle,
  userListComponentEmptyStyle,
  dialogFooterStyle,
  inputStyle,
  dialogFooterButtonStyle,
} from "../utils/constants";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  showSuccessToast,
  showErrorToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";

interface EditFeatureProps {
  users: User[];
  feature: FeatureFlag;
  trigger: React.ReactNode;
  prefixedActiveTab?: string;
}
const EditFeature: FC<EditFeatureProps> = ({
  users,
  feature,
  trigger,
  prefixedActiveTab = "groups",
}) => {
  // Hooks
  const { groups } = useFeatureStore();
  const { updateTenantFeatureFlag } = useFeatureFlags();

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchSelectedInputRef = useRef<HTMLInputElement>(null);

  // State
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [deselectedGroups, setDeselectedGroups] = useState<Group[]>([]);
  const [deselectedGroupsFiltered, setDeselectedGroupsFiltered] = useState<
    Group[]
  >([]);

  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedGroupsFiltered, setSelectedGroupsFiltered] = useState<Group[]>(
    []
  );

  const [deselectedUsers, setDeselectedUsers] = useState<User[]>([]);
  const [deselectedUsersFiltered, setDeselectedUsersFiltered] = useState<
    User[]
  >([]);

  const [selectedSpecificUsers, setSelectedSpecificUsers] = useState<User[]>(
    []
  );
  const [selectedUsersFiltered, setSelectedUsersFiltered] = useState<User[]>(
    []
  );

  const [deselectedSearch, setDeselectedSearch] = useState<string>("");
  const [selectedSearch, setSelectedSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>(prefixedActiveTab);

  // Handlers
  const getDisplayedList = <T,>(
    search: string,
    filtered: T[],
    full: T[]
  ): T[] => (search.length > 0 ? filtered : full);

  const handleAddGroup = (groupId: string) => {
    const groupToAdd = groups.find((group) => group.id === groupId);
    if (groupToAdd) {
      setSelectedGroups([...selectedGroups, groupToAdd]);
      setDeselectedGroups(
        deselectedGroups.filter((group) => group.id !== groupId)
      );
    }
  };

  const handleRemoveGroup = (groupId: string) => {
    const groupToRemove = selectedGroups.find((g) => g.id === groupId);
    if (groupToRemove) {
      setSelectedGroups(selectedGroups.filter((g) => g.id !== groupId));
      setDeselectedGroups([...deselectedGroups, groupToRemove]);
    }
  };

  const handleAddUser = (userId: string) => {
    const userToAdd = users.find((user) => user.id === userId);
    if (
      userToAdd &&
      !selectedSpecificUsers.some((user) => user.id === userId)
    ) {
      setSelectedSpecificUsers([...selectedSpecificUsers, userToAdd]);
      setDeselectedUsers(deselectedUsers.filter((user) => user.id !== userId));
    }
  };

  const handleRemoveUser = (userId: string) => {
    const userToRemove = selectedSpecificUsers.find((u) => u.id === userId);
    if (userToRemove && !deselectedUsers.some((user) => user.id === userId)) {
      setSelectedSpecificUsers(
        selectedSpecificUsers.filter((u) => u.id !== userId)
      );
      setDeselectedUsers([...deselectedUsers, userToRemove]);
    }
  };

  const handleAddAllGroups = () => {
    setSelectedGroups([...selectedGroups, ...deselectedGroups]);
    setDeselectedGroups([]);
  };

  const handleRemoveAllGroups = () => {
    setDeselectedGroups([...deselectedGroups, ...selectedGroups]);
    setSelectedGroups([]);
  };

  const handleAddAllUsers = () => {
    setSelectedSpecificUsers([...selectedSpecificUsers, ...deselectedUsers]);
    setDeselectedUsers([]);
  };

  const handleRemoveAllUsers = () => {
    setDeselectedUsers([...deselectedUsers, ...selectedSpecificUsers]);
    setSelectedSpecificUsers([]);
  };

  const handleSaveFeature = async () => {
    try {
      setIsSaving(true);
      await updateTenantFeatureFlag.mutateAsync({
        strategyId: feature.strategyId,
        featureName: feature.name,
        userIds: selectedSpecificUsers.map((user) => user.id),
        groupIds: selectedGroups.map((group) => group.id),
      });

      setTimeout(() => {
        showSuccessToast("Feature flag er blevet opdateret med succes.");

        setIsOpen(false);
        setIsSaving(false);
      }, 1000);
    } catch (error) {
      logger.error("Failed to update feature flag", {
        operation: "updateFeatureFlag",
        error,
        featureName: feature.name,
        strategyId: feature.strategyId,
        userCount: selectedSpecificUsers.length,
        groupCount: selectedGroups.length,
      });
      showErrorToast("Der opstod en fejl under opdateringen af feature flag.");
      setIsSaving(false);
    }
  };

  // What the discard guard protects: the chosen groups and users, compared
  // with the feature as it was opened.
  const idsOf = (items: { id: string }[]) =>
    items
      .map((item) => item.id)
      .sort()
      .join(",");
  const isDirty =
    idsOf(selectedGroups) !== idsOf(feature.groups) ||
    idsOf(selectedSpecificUsers) !== idsOf(feature.users);

  // Effects: seed from the feature every time the dialog opens, so a discarded
  // edit does not linger into the next open.
  useEffect(() => {
    if (!isOpen) return;
    setDeselectedSearch("");
    setSelectedSearch("");
    setSelectedGroups(feature.groups);
    setSelectedSpecificUsers(feature.users);

    setDeselectedGroups(
      groups.filter((group) => !feature.groups.some((g) => g.id === group.id))
    );
    setDeselectedUsers(
      users.filter((user) => !feature.users.some((u) => u.id === user.id))
    );
  }, [isOpen, groups, feature, users]);

  useEffect(() => {
    if (activeTab === "groups") {
      const filtered = deselectedGroups.filter((group) =>
        group.name?.toLowerCase().includes(deselectedSearch.toLowerCase())
      );
      setDeselectedGroupsFiltered(filtered);
    } else {
      const filtered = deselectedUsers.filter((user) =>
        `${user.firstName} ${user.lastName}`
          .toLowerCase()
          .includes(deselectedSearch.toLowerCase())
      );
      setDeselectedUsersFiltered(filtered);
    }
  }, [deselectedSearch, activeTab, deselectedGroups, deselectedUsers]);

  // Filter selected (right-side) list based on search
  useEffect(() => {
    if (activeTab === "groups") {
      const filtered = selectedGroups.filter((group) =>
        group.name?.toLowerCase().includes(selectedSearch.toLowerCase())
      );
      setSelectedGroupsFiltered(filtered);
    } else {
      const filtered = selectedSpecificUsers.filter((user) =>
        `${user.firstName} ${user.lastName}`
          .toLowerCase()
          .includes(selectedSearch.toLowerCase())
      );
      setSelectedUsersFiltered(filtered);
    }
  }, [selectedSearch, activeTab, selectedGroups, selectedSpecificUsers]);

  return (
    <>
      <Slot onClick={() => setIsOpen(true)}>{trigger}</Slot>
      <FormDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        isDirty={isDirty}
        size="xl"
        className={dialogContentStyle}
      >
        <DialogHeader className={dialogHeaderStyle}>
          <DialogTitle>Redigér {feature.name}</DialogTitle>
        </DialogHeader>

        <DialogDescription asChild className={dialogDescriptionStyle}>
          <div className={dialogDescriptionWrapperStyle}>
            <Tabs
              defaultValue={prefixedActiveTab}
              onValueChange={setActiveTab}
              className="flex-1 flex flex-col min-h-0 workspace-tabs"
            >
              <div className="px-6">
                <TabsList className="flex tabs-list">
                  <TabsTrigger value="groups" className="tab-trigger">
                    Grupper
                  </TabsTrigger>
                  <TabsTrigger value="specificUsers" className="tab-trigger">
                    Specifikke brugere
                  </TabsTrigger>
                </TabsList>
              </div>
              <TooltipProvider>
                <TabsContent
                  value="groups"
                  className="flex-1 min-h-0 data-[state=active]:flex"
                >
                  <div className={userListParentWrapperStyle}>
                    <div className={userListCardWrapperLeft}>
                      <div className={userListCardStyle}>
                        <div className={userListCardHeaderStyle}>
                          <p className={userListCardHeaderTitleTextStyle}>
                            Alle grupper
                          </p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleAddAllGroups}
                                className="mr-2"
                              >
                                <UserPlus className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p
                                className={userListCardHeaderTitleTooltipStyle}
                              >
                                Tilføj alle grupper til featuren
                              </p>
                            </TooltipContent>
                          </Tooltip>

                          <Input
                            type="text"
                            placeholder="Søg"
                            className={inputStyle}
                            onChange={(e) =>
                              setDeselectedSearch(e.target.value)
                            }
                            value={deselectedSearch}
                            ref={searchInputRef}
                          />
                          <div
                            className={
                              userListCardHeaderAbsoluteButtonWrapperStyle
                            }
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (deselectedSearch.length > 0) {
                                  setDeselectedSearch("");
                                } else {
                                  searchInputRef.current?.focus();
                                }
                              }}
                              className={`${userListCardHeaderAbsoluteButtonStyle} ${
                                deselectedSearch.length > 0
                                  ? "opacity-50"
                                  : "opacity-0 hover:opacity-0 cursor-text"
                              }`}
                            >
                              <X className="size-4" />
                            </Button>
                          </div>
                        </div>
                        {getDisplayedList(
                          deselectedSearch,
                          deselectedGroupsFiltered,
                          deselectedGroups
                        ).length > 0 && (
                          <UserList
                            groups={getDisplayedList(
                              deselectedSearch,
                              deselectedGroupsFiltered,
                              deselectedGroups
                            )}
                            onAddGroup={handleAddGroup}
                            className={userListComponentStyle}
                          />
                        )}
                        {getDisplayedList(
                          deselectedSearch,
                          deselectedGroupsFiltered,
                          deselectedGroups
                        ).length === 0 && (
                          <p className={userListComponentEmptyStyle}>
                            Ingen grupper fundet
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={userListCardWrapperRight}>
                      <div className={userListCardStyle}>
                        <div className={userListCardHeaderStyle}>
                          <p className={userListCardHeaderTitleTextStyle}>
                            Valgte grupper
                          </p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleRemoveAllGroups}
                                className="mr-2"
                              >
                                <UserX className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p
                                className={userListCardHeaderTitleTooltipStyle}
                              >
                                Fjern alle grupper fra featuren
                              </p>
                            </TooltipContent>
                          </Tooltip>
                          <Input
                            type="text"
                            placeholder="Søg"
                            className={inputStyle}
                            onChange={(e) => setSelectedSearch(e.target.value)}
                            value={selectedSearch}
                            ref={searchSelectedInputRef}
                          />
                          <div
                            className={
                              userListCardHeaderAbsoluteButtonWrapperStyle
                            }
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (selectedSearch.length > 0) {
                                  setSelectedSearch("");
                                } else {
                                  // Focus the input when clicking the button with empty input
                                  searchSelectedInputRef.current?.focus();
                                }
                              }}
                              className={`${userListCardHeaderAbsoluteButtonStyle} ${
                                selectedSearch.length > 0
                                  ? "opacity-50"
                                  : "opacity-0 hover:opacity-0 cursor-text"
                              }`}
                            >
                              <X className="size-4" />
                            </Button>
                          </div>
                        </div>
                        {getDisplayedList(
                          selectedSearch,
                          selectedGroupsFiltered,
                          selectedGroups
                        ).length > 0 && (
                          <UserList
                            groups={getDisplayedList(
                              selectedSearch,
                              selectedGroupsFiltered,
                              selectedGroups
                            )}
                            onRemoveGroup={handleRemoveGroup}
                            className={userListComponentStyle}
                          />
                        )}
                        {getDisplayedList(
                          selectedSearch,
                          selectedGroupsFiltered,
                          selectedGroups
                        ).length === 0 &&
                          selectedSearch.length === 0 && (
                            <p className={userListComponentEmptyStyle}>
                              Ingen grupper valgt
                            </p>
                          )}
                        {getDisplayedList(
                          selectedSearch,
                          selectedGroupsFiltered,
                          selectedGroups
                        ).length === 0 &&
                          selectedSearch.length > 0 && (
                            <p className={userListComponentEmptyStyle}>
                              Ingen grupper fundet
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent
                  value="specificUsers"
                  className="flex-1 min-h-0 data-[state=active]:flex"
                >
                  <div className={userListParentWrapperStyle}>
                    <div className={userListCardWrapperLeft}>
                      <div className={userListCardStyle}>
                        <div className={userListCardHeaderStyle}>
                          <p className={userListCardHeaderTitleTextStyle}>
                            Alle brugere
                          </p>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleAddAllUsers}
                                className="mr-2"
                              >
                                <UserPlus className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p
                                className={userListCardHeaderTitleTooltipStyle}
                              >
                                Tilføj alle brugere til featuren
                              </p>
                            </TooltipContent>
                          </Tooltip>
                          <Input
                            type="text"
                            placeholder="Søg"
                            className={inputStyle}
                            onChange={(e) =>
                              setDeselectedSearch(e.target.value)
                            }
                            value={deselectedSearch}
                            ref={searchInputRef}
                          />
                          <div
                            className={
                              userListCardHeaderAbsoluteButtonWrapperStyle
                            }
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (deselectedSearch.length > 0) {
                                  setDeselectedSearch("");
                                } else {
                                  searchInputRef.current?.focus();
                                }
                              }}
                              className={`${userListCardHeaderAbsoluteButtonStyle} ${
                                deselectedSearch.length > 0
                                  ? "opacity-50"
                                  : "opacity-0 hover:opacity-0 cursor-text"
                              }`}
                            >
                              <X className="size-4" />
                            </Button>
                          </div>
                        </div>
                        {getDisplayedList(
                          deselectedSearch,
                          deselectedUsersFiltered,
                          deselectedUsers
                        ).length > 0 && (
                          <UserList
                            users={getDisplayedList(
                              deselectedSearch,
                              deselectedUsersFiltered,
                              deselectedUsers
                            )}
                            onAddUser={handleAddUser}
                            className={userListComponentStyle}
                          />
                        )}
                        {getDisplayedList(
                          deselectedSearch,
                          deselectedUsersFiltered,
                          deselectedUsers
                        ).length === 0 &&
                          deselectedSearch.length === 0 && (
                            <p className={userListComponentEmptyStyle}>
                              Ingen brugere valgt
                            </p>
                          )}
                        {getDisplayedList(
                          deselectedSearch,
                          deselectedUsersFiltered,
                          deselectedUsers
                        ).length === 0 &&
                          deselectedSearch.length > 0 && (
                            <p className={userListComponentEmptyStyle}>
                              Ingen brugere fundet
                            </p>
                          )}
                      </div>
                    </div>

                    <div className={userListCardWrapperRight}>
                      <div className={userListCardStyle}>
                        <div className={userListCardHeaderStyle}>
                          <p className={userListCardHeaderTitleTextStyle}>
                            Valgte brugere
                          </p>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleRemoveAllUsers}
                                className="mr-2"
                              >
                                <UserX className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p
                                className={userListCardHeaderTitleTooltipStyle}
                              >
                                Fjern alle brugere fra featuren
                              </p>
                            </TooltipContent>
                          </Tooltip>
                          <Input
                            type="text"
                            placeholder="Søg"
                            className={inputStyle}
                            onChange={(e) => setSelectedSearch(e.target.value)}
                            value={selectedSearch}
                            ref={searchSelectedInputRef}
                          />
                          <div
                            className={
                              userListCardHeaderAbsoluteButtonWrapperStyle
                            }
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (selectedSearch.length > 0) {
                                  setSelectedSearch("");
                                } else {
                                  searchSelectedInputRef.current?.focus();
                                }
                              }}
                              className={`${userListCardHeaderAbsoluteButtonStyle} ${
                                selectedSearch.length > 0
                                  ? "opacity-50"
                                  : "opacity-0 hover:opacity-0 cursor-text"
                              }`}
                            >
                              <X className="size-4" />
                            </Button>
                          </div>
                        </div>
                        {getDisplayedList(
                          selectedSearch,
                          selectedUsersFiltered,
                          selectedSpecificUsers
                        ).length > 0 && (
                          <UserList
                            users={getDisplayedList(
                              selectedSearch,
                              selectedUsersFiltered,
                              selectedSpecificUsers
                            )}
                            onRemoveUser={handleRemoveUser}
                            className={userListComponentStyle}
                          />
                        )}
                        {getDisplayedList(
                          selectedSearch,
                          selectedUsersFiltered,
                          selectedSpecificUsers
                        ).length === 0 &&
                          selectedSearch.length === 0 && (
                            <p className={userListComponentEmptyStyle}>
                              Ingen brugere valgt
                            </p>
                          )}
                        {getDisplayedList(
                          selectedSearch,
                          selectedUsersFiltered,
                          selectedSpecificUsers
                        ).length === 0 &&
                          selectedSearch.length > 0 && (
                            <p className={userListComponentEmptyStyle}>
                              Ingen brugere fundet
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </TooltipProvider>
            </Tabs>
          </div>
        </DialogDescription>
        <DialogFooter className={dialogFooterStyle}>
          <FormDialogCancel disabled={isSaving} />
          <Button
            variant="default"
            onClick={handleSaveFeature}
            className={dialogFooterButtonStyle}
            disabled={isSaving}
          >
            {isSaving ? (
              <Spinner className="size-4 text-primary-foreground" />
            ) : (
              <>Gem</>
            )}
          </Button>
        </DialogFooter>
      </FormDialog>
    </>
  );
};

export default EditFeature;
